import { Card, Button, BlockStack, Text, Badge, ButtonGroup } from '@shopify/polaris';
import { useResource } from '../../../shared/hooks/useResource';
import { useResourceMutation } from '../../../shared/hooks/useResourceMutation';
import { LoadingState } from '../../../shared/components/LoadingState';
import { ErrorBanner } from '../../../shared/components/ErrorBanner';
import {
  CarrierServiceStatus,
  getCarrierServiceStatus,
  registerCarrierService,
  unregisterCarrierService,
} from './api';

export interface CarrierServiceStatusCardProps {
  shopDomain: string;
}

export function CarrierServiceStatusCard({ shopDomain }: CarrierServiceStatusCardProps): JSX.Element {
  const query = useResource<CarrierServiceStatus>(
    ['carrier-service', shopDomain],
    (api) => getCarrierServiceStatus(api, shopDomain),
    Boolean(shopDomain),
  );

  const registerMutation = useResourceMutation(
    (api, domain: string) => registerCarrierService(api, domain),
    [['carrier-service', shopDomain]],
  );

  const unregisterMutation = useResourceMutation(
    (api, args: { id: string; shopDomain: string }) => unregisterCarrierService(api, args),
    [['carrier-service', shopDomain]],
  );

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">Shopify CarrierService</Text>

        {query.isLoading ? <LoadingState message="Checking Shopify..." /> : null}
        {query.error ? <ErrorBanner error={query.error} /> : null}
        {registerMutation.error ? <ErrorBanner error={registerMutation.error} /> : null}
        {unregisterMutation.error ? <ErrorBanner error={unregisterMutation.error} /> : null}

        {query.data ? (
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              Status:{' '}
              {query.data.registered ? (
                <Badge tone="success">Registered</Badge>
              ) : (
                <Badge tone="warning">Not registered</Badge>
              )}
            </Text>
            {query.data.callbackUrl ? (
              <Text as="p" variant="bodyMd">
                Callback URL: <code>{query.data.callbackUrl}</code>
              </Text>
            ) : null}
            {query.data.carrierServiceId ? (
              <Text as="p" variant="bodyMd">
                CarrierService ID: <code>{query.data.carrierServiceId}</code>
              </Text>
            ) : null}

            <ButtonGroup>
              {!query.data.registered ? (
                <Button
                  variant="primary"
                  loading={registerMutation.isPending}
                  onClick={() => registerMutation.mutate(shopDomain)}
                >
                  Register CarrierService
                </Button>
              ) : (
                <>
                  <Button
                    loading={registerMutation.isPending}
                    onClick={() => registerMutation.mutate(shopDomain)}
                  >
                    Re-register
                  </Button>
                  <Button
                    tone="critical"
                    loading={unregisterMutation.isPending}
                    onClick={() =>
                      query.data?.carrierServiceId &&
                      unregisterMutation.mutate({
                        id: query.data.carrierServiceId,
                        shopDomain,
                      })
                    }
                  >
                    Unregister
                  </Button>
                </>
              )}
            </ButtonGroup>
          </BlockStack>
        ) : null}
      </BlockStack>
    </Card>
  );
}
